import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from 'react-router-dom';

import { Alert, Button, Stack } from 'react-bootstrap';

import api from 'api';

import useRequest from 'hooks/useRequest';
import useTableState from 'hooks/useTableState';
import { formatUTCDate } from 'utils/dateTime';

import Table from 'components/elements/Table';
import ListToolbar from 'components/layout/ListToolbar';
import SectionWrapper from 'components/layout/SectionWrapper';

import UserListFilter from 'components/elements/UserListFilter';

import { UserType } from 'types/user';
import { RolesLabels } from 'utils/enums';
import useUser from 'hooks/useUser';
import FilterEnum from './filters/data';

type FilterEnumType = typeof FilterEnum;
type FilterKey = FilterEnumType[keyof FilterEnumType];

type FilterType = {
  [_key in FilterKey]: number | string | boolean | null;
};

type ListDataResponse = {
  data: UserType[];
  count: number;
  page: number;
  pageSize: number;
  pageCount: number;
};

const columns = [
  {
    id: 'name',
    label: 'Name',
    headerStyle: { width: '15%' },
  },
  {
    id: 'email',
    label: 'Username',
    headerStyle: { width: '15%' },
  },
  {
    id: 'burnCenterName',
    label: 'Burn Center',
    headerStyle: { width: '20%' },
    override: ({ burnCenterId, burnCenterName }: UserType) => burnCenterName ?? burnCenterId ?? '',
  },
  {
    id: 'role',
    label: 'Role',
    headerStyle: { width: '10%' },
    override: ({ role }: UserType) => (role ? RolesLabels[role] ?? '' : ''),
  },
  {
    id: 'canAccessVerification',
    label: 'Verification',
    headerStyle: { width: '10%' },
    override: ({ canAccessVerification }: UserType) => (canAccessVerification ? 'Yes' : 'No'),
  },
  {
    id: 'clinicalStudioRole',
    label: 'Data Entry',
    headerStyle: { width: '10%' },
  },
  {
    id: 'reportingRole',
    label: 'Reporting',
    headerStyle: { width: '8%' },
  },
  {
    id: 'createdAt',
    label: 'Created',
    headerStyle: { width: '8%' },
    override: ({ createdAt }: UserType) => formatUTCDate(createdAt),
  },
  {
    id: 'lastLoginAt',
    label: 'Last Signed In',
    headerStyle: { width: '9%' },
    override: ({ lastLoginAt }: UserType) => formatUTCDate(lastLoginAt),
  },
  {
    id: 'active',
    label: 'Active',
    override: ({ disabledAt }: UserType) => (disabledAt ? 'No' : 'Yes'),
  },
];

const UsersList: React.FC<{ source?: string }> = ({ source }) => {
  const navigate = useNavigate();
  const {
    appUser, isBdataAmin, isBcAdmin, isAbaAdmin,
  } = useUser();
  const { pathname } = useLocation();
  const { id: burnCenterId } = useParams<{ id: string, userId: string }>();
  const canViewAll = isBdataAmin || isAbaAdmin;
  const canAddUser = isBdataAmin || isBcAdmin;
  const isAllowedRole = isBdataAmin || isAbaAdmin || isBcAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const includeInactive = searchParams.get('includeInactive') || '';
  const hideBurnCenterColumn = source === 'burn-centers';

  const visibleColumns = useMemo(
    () => (hideBurnCenterColumn
      ? columns
        .filter(({ id }) => id !== 'burnCenterName')
        .map((column) => (column.id === 'role'
          ? {
            ...column,
            headerStyle: { width: 'auto' },
          }
          : column))
      : columns),
    [hideBurnCenterColumn],
  );

  const initFilters: FilterType = {
    [FilterEnum.search]: searchQuery || null,
    [FilterEnum.showInactive]: includeInactive || false,
  };

  const {
    isLoading,
    error,
    doRequest: getListData,
    data: listData,
  } = useRequest<ListDataResponse>(api.getUsers);

  const {
    page,
    handlePageChange,
    sortBy,
    sortOrder,
    handleSortChange,
    filters,
    handleFilterChange,
  } = useTableState<FilterType>(columns[0].id, 'asc', initFilters);

  const requestParams = useMemo(() => {
    const base = {
      sortBy,
      sortOrder,
      ...filters,
    };

    if (!canViewAll && isBcAdmin && appUser?.burnCenterId) {
      return {
        ...base,
        burnCenterId: appUser.burnCenterId,
      };
    }

    if (burnCenterId) {
      return {
        ...base,
        burnCenterId,
      };
    }

    return base;
  }, [sortBy, sortOrder, filters, canViewAll, isBcAdmin, appUser?.burnCenterId, burnCenterId]);

  const fetchListData = useCallback(() => {
    getListData({
      ...requestParams,
      Page: page,
    });
  }, [getListData, page, requestParams]);

  useEffect(() => {
    fetchListData();
  }, [fetchListData]);

  return (
    <SectionWrapper title={!burnCenterId ? 'Users' : ''}>
      <Stack gap={3} style={{ minWidth: 0 }}>
        {isAllowedRole && (
          <ListToolbar
            className={!canAddUser ? 'justify-content-end' : ''}
            left={
              canAddUser ? (
                <Button as={Link as any} to="new" variant="secondary">
                  Add User
                </Button>
              ) : null
            }
            right={(
              <div style={{ width: '100%', maxWidth: '760px', minWidth: 0 }}>
                <UserListFilter
                  initialSearch={(filters[FilterEnum.search] as string) ?? ''}
                  initialShowInactive={(filters[FilterEnum.showInactive] as boolean) ?? false}
                  onChange={({ search, showInactive }) => {
                    setSearchParams((prev) => {
                      const p = new URLSearchParams(prev);
                      if (search) p.set('search', search);
                      else p.delete('search');
                      p.set('includeInactive', String(showInactive));

                      return p;
                    });

                    handleFilterChange(FilterEnum.search, search);
                    handleFilterChange(FilterEnum.showInactive, showInactive);
                  }}
                  burnCenterId={burnCenterId ? Number(burnCenterId) : undefined}
                />
              </div>
            )}
          />
        )}
        {error ? (
          <Alert variant="danger">Error Loading List</Alert>
        ) : (
          <Table<UserType>
            columns={visibleColumns}
            items={listData?.data || []}
            minTableWidth="980px"
            handlePageChange={handlePageChange}
            handleOrderChange={handleSortChange}
            order={sortBy}
            orderDirection={sortOrder}
            pagination={{
              total: listData?.count || 0,
              limit: listData?.pageSize || 10,
              page,
            }}
            onClickRow={
              isAllowedRole
                ? ({ id }: UserType) => navigate(`${pathname}/${id}`)
                : undefined
            }
            isLoading={isLoading}
          />
        )}
      </Stack>
    </SectionWrapper>
  );
};

export default UsersList;
